import React, { useState, useEffect } from 'react'
import useQueryParams from '../../../../hooks/useQueryParams'
import useDateRangeFilter from '../../../../hooks/useDateRangeFilter'

import ReportTable from '../../../../templates/ReportTable/ReportTable'
import callAPI from '../../../../helpers/callAPI'
import Loading from '../../../../templates/UI/Loading/Loading'
import { Link } from 'react-router-dom'
import Dollars from '../../../../templates/Dollars/Dollars'
import Number from '../../../../templates/Number/Number'
import ReportsStatsDropdown from '../../Templates/ReportsStatsDropdown/ReportsStatsDropdown'
import numberSort from '../../../../helpers/numberSort'

export default function CreativesReport({ showSystemMessage, setAffiliate, setCampaign, setAdvertiser, setCSVFilename, setCSVHeaderLabels, setCSVDataLabels, setCSVReport }) {
    const { initStartDate, initEndDate } = useDateRangeFilter()
    const { affiliateId, campaignId, advertiserId, navigation, buildUrl, hideNetwork, withAvatar } = useQueryParams()

    const [dataLoaded, setDataLoaded] = useState(false)
    const [report, setReport] = useState([])
    const [totals, setTotals] = useState([])
    const [hasForex, setHasForex] = useState(false)
    const [forexCurrency, setForexCurrency] = useState(null)

    useEffect(() => {
        if (hideNetwork === 'true') {
            setCSVHeaderLabels(['Creative ID', 'Clicks', 'Conversions', 'Conversion Ratio', 'Aff Earnings', 'Sales', 'EPC'])
            setCSVDataLabels(['creative_id', 'clicks', 'conversions', 'conversion_ratio', 'affiliate_earnings', 'sales', 'epc'])
        } else {
            const baseHeaders = ['Creative ID', 'Clicks', 'Conversions', 'Conversion Ratio']
            const baseLabels = ['creative_id', 'clicks', 'conversions', 'conversion_ratio']
            if (hasForex) {
                setCSVHeaderLabels([...baseHeaders, `Net Earnings (${(forexCurrency || 'FOREX').toUpperCase()})`, 'Net Earnings', 'Aff Earnings', 'Sales', 'Margin', 'EPC', 'nEPC'])
                setCSVDataLabels([...baseLabels, 'orig_net_earnings', 'network_earnings', 'affiliate_earnings', 'sales', 'margin', 'epc', 'nepc'])
            } else {
                setCSVHeaderLabels([...baseHeaders, 'Net Earnings', 'Aff Earnings', 'Sales', 'Margin', 'EPC', 'nEPC'])
                setCSVDataLabels([...baseLabels, 'network_earnings', 'affiliate_earnings', 'sales', 'margin', 'epc', 'nepc'])
            }
        }
    }, [hideNetwork, hasForex, forexCurrency])

    useEffect(() => {
        getReport()
    }, [])

    const getReport = async () => {
        setDataLoaded(false)

        // We grab the data using the INITIALIZED dates. Hitting refresh turns selected options into init options.
        let url = buildUrl(`reports/creatives?startDate=${initStartDate}&endDate=${initEndDate}`)

        callAPI(url, 'GET')
            .then((result) => {
                if (result['success'] === true) {
                    result.affiliate && setAffiliate(result.affiliate)
                    result.campaign && setCampaign(result.campaign)
                    result.advertiser && setAdvertiser(result.advertiser)
                    setReport(result.report)
                    setTotals(result.totals)
                    setHasForex(!!result.hasForex)
                    setForexCurrency(result.forexCurrency || null)
                    setCSVReport(result.report)
                    setCSVFilename(`Creatives Report - ${initStartDate}-${initEndDate}`)
                    setDataLoaded(true)
                } else if (result['success'] === false) {
                    showSystemMessage('error', result.errors.message)
                    setDataLoaded(true)
                } else {
                    showSystemMessage('error', 'Could not load report.')
                    setDataLoaded(true)
                }
            })
            .catch((error) => {
                console.log(`Error while fetching earnings report: ${error.message}`)
                showSystemMessage('error', `An error occurred while fetching earnings report: ${error.message}`)
                setDataLoaded(true)
            })
    }

    const getReportOutput = () => {
        const showForexColumn = hideNetwork !== 'true' && hasForex

        let columns = [
            {
                Header: 'Creative Id',
                sortType: numberSort,
                accessor: 'creative_id',
                Cell: ({ cell: { value } }) => {
                    return <>{value}</>
                },
            },
            {
                Header: () => <div style={{ textAlign: 'right' }}>Clicks</div>,
                sortType: numberSort,
                accessor: 'clicks',
                className: 'text-right',
                Cell: (tableData) => {
                    return <Link to={buildUrl(`/reports/stats/daily?startDate=${initStartDate}&endDate=${initEndDate}&creativeId=${tableData.data[tableData.row.index].creative_id}`)}>{`${Intl.NumberFormat('en-GB').format(tableData.data[tableData.row.index].clicks)}`}</Link>
                }
            },
            {
                Header: () => <div style={{ textAlign: 'right' }}>Conversions</div>,
                sortType: numberSort,
                accessor: 'conversions',
                className: 'text-right',
                Cell: (tableData) => {
                    return <Link to={buildUrl(`/reports/stats/conversions?startDate=${initStartDate}&endDate=${initEndDate}&creativeId=${tableData.data[tableData.row.index].creative_id}`)}>{`${Intl.NumberFormat('en-GB').format(tableData.data[tableData.row.index].conversions)}`}</Link>
                },
            },
            {
                Header: () => <div style={{ textAlign: 'right' }}>CR %</div>,
                sortType: numberSort,
                accessor: 'conversion_ratio',
                className: 'text-right',
                Cell: ({ cell: { value } }) => {
                    return <>{!isNaN(value) ? <Number value={value} decimals={2} /> : 0} %</>
                },
            },
            ...(showForexColumn
                ? [
                      {
                          Header: () => <div style={{ textAlign: 'right' }}>
                            Net Earnings ({(forexCurrency || 'FOREX').toUpperCase()})
                            </div>,
                          sortType: numberSort,
                          accessor: 'orig_net_earnings',
                          className: 'text-right',
                          Cell: ({ cell: { value } }) => {
                              return <Dollars currency={(forexCurrency || 'USD')} value={value} />
                          },
                      },
                  ]
                : []),
            {
                Header: () => <div style={{ textAlign: 'right' }}>Net Earnings</div>,
                sortType: numberSort,
                accessor: 'network_earnings',
                className: 'text-right',
                Cell: ({ cell: { value } }) => {
                    return <Dollars value={value} />
                },
            },
            {
                Header: () => <div style={{ textAlign: 'right' }}>Aff Earnings</div>,
                sortType: numberSort,
                accessor: 'affiliate_earnings',
                className: 'text-right',
                Cell: ({ cell: { value } }) => {
                    return <Dollars value={value} />
                },
            },
            {
                Header: () => <div style={{ textAlign: 'right' }}>Sales</div>,
                sortType: numberSort,
                accessor: 'sales',
                className: 'text-right',
                Cell: ({ cell: { value } }) => {
                    return <Dollars value={value} />
                },
            },
            {
                Header: () => <div style={{ textAlign: 'right' }}>Margin</div>,
                sortType: numberSort,
                accessor: 'margin',
                className: 'text-right',
                Cell: ({ cell: { value } }) => {
                    return <Dollars value={value} />
                },
            },
            {
                Header: () => <div style={{ textAlign: 'right' }}>EPC</div>,
                sortType: numberSort,
                accessor: 'epc',
                className: 'text-right',
                Cell: ({ cell: { value } }) => {
                    return <Dollars value={value} />
                },
            },
            {
                Header: () => <div style={{ textAlign: 'right' }}>nEPC</div>,
                sortType: numberSort,
                accessor: 'nepc',
                className: 'text-right',
                Cell: ({ cell: { value } }) => {
                    return <Dollars value={value} />
                },
            },
            {
                Header: ' ',
                className: 'text-right',
                Cell: (tableData) => {
                    return <ReportsStatsDropdown reportType="creatives" creativeId={tableData.data[tableData.row.index].creative_id} campaignId={campaignId} advertiserId={advertiserId} affiliateId={affiliateId} navigation={navigation} withAvatar={withAvatar} showSystemMessage={showSystemMessage} />
                },
            },
        ]

        if (hideNetwork == 'true') {
            columns = [
                {
                    Header: 'Creative Id',
                    sortType: numberSort,
                    accessor: 'creative_id',
                    Cell: ({ cell: { value } }) => {
                        return <>{value}</>
                    },
                },
                {
                    Header: () => <div style={{ textAlign: 'right' }}>Clicks</div>,
                    sortType: numberSort,
                    accessor: 'clicks',
                    className: 'text-right',
                    Cell: (tableData) => {
                        return <Link to={buildUrl(`/reports/stats/daily?startDate=${initStartDate}&endDate=${initEndDate}&creativeId=${tableData.data[tableData.row.index].creative_id}`)}>{`${Intl.NumberFormat('en-GB').format(tableData.data[tableData.row.index].clicks)}`}</Link>
                    }
                },
                {
                    Header: () => <div style={{ textAlign: 'right' }}>Conversions</div>,
                    sortType: numberSort,
                    accessor: 'conversions',
                    className: 'text-right',
                    Cell: (tableData) => {
                        return <Link to={buildUrl(`/reports/stats/conversions?startDate=${initStartDate}&endDate=${initEndDate}&creativeId=${tableData.data[tableData.row.index].creative_id}`)}>{`${Intl.NumberFormat('en-GB').format(tableData.data[tableData.row.index].conversions)}`}</Link>
                    },
                },
                {
                    Header: () => <div style={{ textAlign: 'right' }}>CR %</div>,
                    sortType: numberSort,
                    accessor: 'conversion_ratio',
                    className: 'text-right',
                    Cell: ({ cell: { value } }) => {
                        return <><Number value={value} decimals={2} /> %</>
                    },
                },
                {
                    Header: () => <div style={{ textAlign: 'right' }}>Aff Earnings</div>,
                    sortType: numberSort,
                    accessor: 'affiliate_earnings',
                    className: 'text-right',
                    Cell: ({ cell: { value } }) => {
                        return <Dollars value={value} />
                    },
                },
                {
                    Header: () => <div style={{ textAlign: 'right' }}>Sales</div>,
                    sortType: numberSort,
                    accessor: 'sales',
                    className: 'text-right',
                    Cell: ({ cell: { value } }) => {
                        return <Dollars value={value} />
                    },
                },
                {
                    Header: () => <div style={{ textAlign: 'right' }}>EPC</div>,
                    sortType: numberSort,
                    accessor: 'epc',
                    className: 'text-right',
                    Cell: ({ cell: { value } }) => {
                        return <Dollars value={value} />
                    },
                },
                {
                    Header: ' ',
                    className: 'text-right',
                    Cell: (tableData) => {
                        return <ReportsStatsDropdown reportType="creatives" creativeId={tableData.data[tableData.row.index].creative_id} campaignId={campaignId} advertiserId={advertiserId} affiliateId={affiliateId} navigation={navigation} withAvatar={withAvatar} showSystemMessage={showSystemMessage} />
                    },
                },
            ]
        }

        return (
            <React.Fragment>
                <ReportTable data={report} columns={columns} totals={totals} />
            </React.Fragment>
        )
    }

    return <div>{dataLoaded ? report.length === 0 ? <div className="p-3">No stats for the current parameters.</div> : getReportOutput() : <Loading></Loading>}</div>
}
